import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { PageHead, Panel } from "@/project/components/dashboard-shell";
import { Upload, CheckCircle2, BookOpen, Image, Tag, AlertCircle, Send } from "lucide-react";

export const Route = createFileRoute("/institution/impact")({ component: InstitutionImpact });

const CATEGORIES = [
  "Animal Rescue", "Medical Recovery", "Rehabilitation & Release",
  "Shelter Expansion", "Bird Care", "Feed Drive", "Volunteer Day", "Other",
];

function InstitutionImpact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    title: "",
    animalName: "",
    species: "",
    category: "",
    body: "",
    outcome: "",
    tags: "",
  });
  const [photos, setPhotos] = useState<string[]>([]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <>
        <PageHead title="Story Submitted" subtitle="Your impact story has been sent for review." />
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="grid h-20 w-20 place-items-center rounded-full bg-green-100 text-green-600 mb-5">
            <CheckCircle2 className="h-10 w-10" />
          </div>
          <h2 className="font-display text-2xl font-bold text-[var(--maroon-deep)]">Story submitted for review!</h2>
          <p className="mt-3 max-w-sm text-sm text-foreground/70">
            Our team will review your story within 48 hours. Once approved, it will appear on the public Impact Stories page and in your donors' feeds.
          </p>
          <button onClick={() => { setSubmitted(false); setForm({ title: "", animalName: "", species: "", category: "", body: "", outcome: "", tags: "" }); setPhotos([]); }}
            className="mt-6 rounded-full border border-[var(--maroon)]/30 px-6 py-2.5 text-sm font-semibold text-[var(--maroon)] hover:bg-[var(--cream-2)] transition">
            Submit another story
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHead
        title="Submit Impact Story"
        subtitle="Share a rescue or rehabilitation story. Stories approved by admin appear on the public website and donor feeds."
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Story basics */}
          <Panel title="Story Details">
            <div className="space-y-4">
              <Field label="Story Title *" hint="Keep it impactful — e.g. 'Rescued cow Nandini walks again after 3 months'">
                <input required value={form.title} onChange={set("title")} placeholder="Enter a compelling title…"
                  className="input-base" />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Animal Name (if any)">
                  <input value={form.animalName} onChange={set("animalName")} placeholder="e.g. Nandini"
                    className="input-base" />
                </Field>
                <Field label="Species">
                  <input value={form.species} onChange={set("species")} placeholder="e.g. Cow, Pigeon, Stray Dog"
                    className="input-base" />
                </Field>
              </div>

              <Field label="Category *">
                <select required value={form.category} onChange={set("category")} className="input-base">
                  <option value="">Select a category…</option>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
            </div>
          </Panel>

          {/* Story body */}
          <Panel title="Tell the Story">
            <div className="space-y-4">
              <Field label="Full Story *" hint="Describe what happened, how the animal was found, what treatment was given.">
                <textarea required value={form.body} onChange={set("body")} rows={8}
                  placeholder="On the morning of August 3rd, our team received a call about an injured cow on the highway near…"
                  className="input-base resize-none" />
                <p className="mt-1 text-right text-[11px] text-foreground/50">{form.body.length} / 2000 chars</p>
              </Field>

              <Field label="Outcome / Current Status *" hint="Where is the animal now? Was it released, adopted, or is it still in care?">
                <textarea required value={form.outcome} onChange={set("outcome")} rows={3}
                  placeholder="Nandini has fully recovered and is now thriving at our gaushala. She will remain in our care…"
                  className="input-base resize-none" />
              </Field>

              <Field label="Tags" hint="Comma-separated. e.g. Road Accident, Surgery, Recovery, Adoption">
                <input value={form.tags} onChange={set("tags")} placeholder="Road Accident, Surgery, Recovery"
                  className="input-base" />
              </Field>
            </div>
          </Panel>

          {/* Photos */}
          <Panel title="Photos">
            <div className="space-y-3">
              <p className="text-xs text-foreground/60">Upload at least one photo. Before/after photos are highly effective and increase donor engagement.</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {photos.map((p, i) => (
                  <div key={i} className="relative aspect-square rounded-xl bg-muted/40 overflow-hidden">
                    <img src={p} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                      className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white text-xs hover:bg-black">✕</button>
                  </div>
                ))}
                <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border/60 hover:border-[var(--maroon)]/40 hover:bg-[var(--cream-2)] transition">
                  <Image className="h-6 w-6 text-foreground/40" />
                  <span className="text-[11px] text-foreground/50">Add photo</span>
                  <input type="file" accept="image/*" className="sr-only" multiple
                    onChange={() => {
                      // Simulate adding placeholder images for demo
                      setPhotos((p) => [...p, `https://images.unsplash.com/photo-${Date.now()}?w=300&q=80`]);
                    }} />
                </label>
              </div>
              <p className="text-[11px] text-foreground/50 flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                Photos must be from your institution and not violate animal welfare guidelines.
              </p>
            </div>
          </Panel>

          <button type="submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--maroon)] py-3.5 text-sm font-bold text-white hover:bg-[var(--maroon-deep)] transition">
            <Send className="h-4 w-4" /> Submit Story for Review
          </button>
        </form>

        {/* ── Sidebar tips ── */}
        <div className="space-y-4">
          <Panel title="Writing Tips">
            <ul className="space-y-3 text-sm text-foreground/70">
              {[
                { icon: "✏️", tip: "Give the animal a name — it makes the story personal" },
                { icon: "📸", tip: "Before/after photos increase donor trust by 3x" },
                { icon: "📅", tip: "Include dates and timeline of recovery" },
                { icon: "💰", tip: "Mention how donor funds were specifically used" },
                { icon: "❤️", tip: "End on a positive note — current health or adoption" },
              ].map((t) => (
                <li key={t.tip} className="flex gap-2.5">
                  <span className="shrink-0">{t.icon}</span>
                  <p className="leading-snug">{t.tip}</p>
                </li>
              ))}
            </ul>
          </Panel>

          <Panel title="Review Process">
            <ol className="space-y-3 text-sm text-foreground/70">
              {[
                "You submit the story",
                "Admin reviews within 48 hours",
                "If approved, story goes live on public website",
                "Your donors receive a notification",
                "Story appears on your institution profile",
              ].map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--maroon)]/10 text-[11px] font-bold text-[var(--maroon)]">{i + 1}</span>
                  <p className="leading-snug">{step}</p>
                </li>
              ))}
            </ol>
          </Panel>

          <div className="rounded-2xl border border-[var(--saffron-deep)]/20 bg-[var(--saffron)]/8 p-4">
            <BookOpen className="h-5 w-5 text-[var(--saffron-deep)] mb-2" />
            <p className="text-xs font-bold text-[var(--maroon-deep)]">Your past stories</p>
            <p className="mt-1 text-[11px] text-foreground/60">You have published 4 stories. Total reader reach: 2,840 donors.</p>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold text-foreground/80">{label}</label>
      {hint && <p className="mb-2 text-[11px] text-foreground/55">{hint}</p>}
      {children}
    </div>
  );
}